import { motion } from "framer-motion";
import { Star, ArrowUpRight, BadgeCheck, ShieldPlus, ShieldCheck } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SERVICES, BENEFITS, INDUSTRIES, PROCESS, TESTIMONIALS, IMAGES, TRUST_BADGES } from "../lib/content";

/* ---------------- Trust Bar ---------------- */
export const TrustBar = () => (
  <section data-testid="trust-bar" className="border-b border-navy/10 bg-white">
    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-6 px-5 py-10 sm:grid-cols-3 lg:grid-cols-6 lg:px-8">
      {TRUST_BADGES.map((b, i) => {
        const Icon = b.icon;
        return (
          <Reveal key={b.label} delay={i * 0.06}>
            <div className="flex flex-col items-center gap-3 text-center" data-testid={`trust-badge-${i}`}>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy/5">
                <Icon className="h-6 w-6 text-navy" strokeWidth={1.9} />
              </div>
              <span className="text-sm font-semibold leading-tight text-navy">{b.label}</span>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

/* ---------------- Marquee ---------------- */
export const Marquee = () => {
  const words = ["Medical Courier", "Legal Documents", "Same-Day Delivery", "Specimen Transport", "Scheduled Routes", "HIPAA-Conscious"];
  const strip = [...words, ...words];
  return (
    <section data-testid="marquee" className="border-y border-navy/10 bg-navy py-6 overflow-hidden relative">
      <div className="noise-overlay" />
      <div className="flex w-max animate-marquee">
        {strip.map((w, i) => (
          <div key={i} className="flex items-center gap-6 px-6">
            <span className="font-display text-2xl font-bold tracking-tight text-white/90 lg:text-3xl">{w}</span>
            <Star className="h-4 w-4 fill-gold text-gold" />
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------------- Services ---------------- */
export const Services = () => (
  <section id="services" data-testid="services" className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
    <Reveal>
      <Eyebrow testid="services-eyebrow">What We Move</Eyebrow>
      <h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-navy lg:text-5xl">
        Courier solutions built for sensitive, time-critical deliveries
      </h2>
    </Reveal>
    <div className="mt-14 grid gap-6 md:grid-cols-2">
      {SERVICES.map((s, i) => {
        const Icon = s.icon;
        return (
          <Reveal key={s.title} delay={i * 0.08}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ duration: 0.25 }}
              data-testid={`service-card-${i}`}
              className="group h-full rounded-3xl border border-navy/10 bg-white p-8 shadow-[0_8px_32px_rgba(11,46,89,0.05)]"
            >
              <div className="flex items-start justify-between">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy transition-colors duration-300 group-hover:bg-gold">
                  <Icon className="h-7 w-7 text-white transition-colors duration-300 group-hover:text-navy" strokeWidth={1.8} />
                </div>
                <ArrowUpRight className="h-6 w-6 text-navy/20 transition-colors duration-300 group-hover:text-gold" />
              </div>
              <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-navy">{s.title}</h3>
              <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-gold">{s.tagline}</p>
              <ul className="mt-5 grid gap-2.5">
                {s.items.map((it) => (
                  <li key={it} className="flex items-center gap-3 text-sm text-navy/70">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold" /> {it}
                  </li>
                ))}
              </ul>
            </motion.div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

/* ---------------- Why Choose ---------------- */
export const WhyChoose = () => (
  <section id="why" data-testid="why" className="relative overflow-hidden bg-navy py-24 lg:py-32">
    <div className="noise-overlay" />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
      <Reveal>
        <Eyebrow testid="why-eyebrow">Why Wilson</Eyebrow>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white lg:text-5xl">
          Trusted with the deliveries that can't be late
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-5">
        {BENEFITS.map((b, i) => {
          const Icon = b.icon;
          return (
            <Reveal key={b.label} delay={i * 0.05}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ duration: 0.25 }}
                data-testid={`benefit-${i}`}
                className="flex h-full min-h-[172px] flex-col rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition-colors duration-300 hover:border-gold/40 hover:bg-white/10"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gold">
                  <Icon className="h-6 w-6 text-navy" strokeWidth={1.9} />
                </div>
                <p className="mt-5 font-display text-base font-semibold leading-snug text-white lg:text-lg">{b.label}</p>
              </motion.div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- About ---------------- */
export const About = () => (
  <section id="about" data-testid="about" className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
    <div className="grid items-center gap-14 lg:grid-cols-2">
      <Reveal>
        <div className="relative">
          <div className="overflow-hidden rounded-[2rem] border border-navy/10 shadow-[0_20px_60px_rgba(11,46,89,0.15)]">
            <img src={IMAGES.specimen} alt="Secure medical specimen handling" className="aspect-[4/5] w-full object-cover lg:aspect-[4/4.2]" />
          </div>
          <div className="absolute -bottom-6 -right-4 rounded-2xl bg-navy px-7 py-5 shadow-[0_16px_40px_rgba(11,46,89,0.3)] lg:-right-6">
            <p className="font-display text-3xl font-extrabold text-gold">100%</p>
            <p className="text-xs font-medium uppercase tracking-wider text-white/80">Locally owned & operated</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <Eyebrow testid="about-eyebrow">About Us</Eyebrow>
        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-navy lg:text-5xl">
          A courier partner Metro Atlanta businesses depend on
        </h2>
        <div className="mt-6 space-y-5 text-base leading-relaxed text-navy/70">
          <p>Wilson Courier Solutions is a locally owned courier company dedicated to providing dependable, professional delivery services throughout Metro Atlanta.</p>
          <p>We specialize in medical, legal, and business courier solutions with an emphasis on reliability, professionalism, and customer service.</p>
          <p>Whether it's a single urgent delivery or recurring scheduled routes, we help businesses keep operations running smoothly.</p>
        </div>
      </Reveal>
    </div>

    <Reveal>
      <div data-testid="meet-the-owner" className="relative mt-16 overflow-hidden rounded-[2rem] border border-navy/10 bg-navy shadow-[0_20px_60px_rgba(11,46,89,0.15)] lg:mt-24">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[300px] lg:min-h-full">
            <img src={IMAGES.hero} alt="Owner of Wilson Courier Solutions beside a branded delivery van" className="absolute inset-0 h-full w-full object-cover object-[62%_center]" />
          </div>
          <div className="relative p-8 sm:p-10 lg:p-14">
            <div className="noise-overlay" />
            <span className="relative text-xs font-bold uppercase tracking-[0.2em] text-gold">Meet the Owner</span>
            <h3 className="relative mt-4 font-display text-2xl font-bold tracking-tight text-white lg:text-3xl">
              Built on trust, driven by service
            </h3>
            <p className="relative mt-6 text-base leading-relaxed text-white/80">
              "Wilson Courier Solutions was founded to provide dependable, secure, and professional courier services throughout Metro Atlanta. We specialize in same-day medical, legal, and business deliveries while treating every shipment with the urgency and professionalism it deserves. Our commitment is simple: deliver on time, protect every package, and provide exceptional service every step of the way."
            </p>
            <div className="relative mt-7 border-t border-white/10 pt-5">
              <p className="font-display text-lg font-bold text-white">Wilson Courier Solutions</p>
              <p className="text-sm text-gold">Owner &amp; Founder · Metro Atlanta</p>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);

/* ---------------- Industries ---------------- */
export const Industries = () => (
  <section id="industries" data-testid="industries" className="bg-[#F8FAFC] py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <Reveal>
        <Eyebrow testid="industries-eyebrow">Industries We Serve</Eyebrow>
        <h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-navy lg:text-5xl">
          From hospitals to law firms, we deliver
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {INDUSTRIES.map((ind, i) => {
          const Icon = ind.icon;
          return (
            <Reveal key={ind.label} delay={i * 0.04}>
              <motion.div
                whileHover={{ y: -5 }}
                transition={{ duration: 0.25 }}
                data-testid={`industry-${i}`}
                className="group flex items-center gap-4 rounded-2xl border border-navy/10 bg-white p-5 shadow-[0_6px_24px_rgba(11,46,89,0.04)]"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy/5 transition-colors duration-300 group-hover:bg-gold">
                  <Icon className="h-5 w-5 text-navy" strokeWidth={1.9} />
                </div>
                <span className="font-medium text-navy">{ind.label}</span>
              </motion.div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- Process ---------------- */
export const Process = () => (
  <section id="process" data-testid="process" className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
    <Reveal>
      <Eyebrow testid="process-eyebrow">Our Process</Eyebrow>
      <h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-navy lg:text-5xl">
        Four simple steps to a confirmed delivery
      </h2>
    </Reveal>
    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      {PROCESS.map((p, i) => (
        <Reveal key={p.step} delay={i * 0.08}>
          <div data-testid={`process-step-${i}`} className="relative h-full rounded-3xl border border-navy/10 bg-white p-8">
            <span className="font-display text-5xl font-extrabold text-navy/10">{p.step}</span>
            <h3 className="mt-4 font-display text-xl font-bold tracking-tight text-navy">{p.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-navy/60">{p.desc}</p>
            <span className="mt-6 block h-1 w-10 rounded-full bg-gold" />
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- Certifications ---------------- */
export const Certifications = () => {
  const certs = [
    { icon: BadgeCheck, title: "HIPAA Training", desc: "Completed HIPAA training for handling protected health information." },
    { icon: ShieldPlus, title: "Bloodborne Pathogen Training", desc: "Certified in bloodborne pathogen safety for medical specimen transport." },
  ];
  return (
    <section id="certifications" data-testid="certifications" className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
      <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        <Reveal>
          <Eyebrow testid="certifications-eyebrow">Professional Standards</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-navy lg:text-5xl">
            Trained to protect sensitive deliveries
          </h2>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-navy/70">
            <p>Wilson Courier Solutions is committed to secure, professional transportation for medical, legal, and business deliveries.</p>
            <p>We follow professional procedures designed to help protect sensitive medical deliveries and confidential business documents.</p>
          </div>
          <div className="mt-8 flex items-center gap-3 rounded-2xl border border-gold/30 bg-gold/5 px-5 py-4">
            <ShieldCheck className="h-6 w-6 shrink-0 text-gold" />
            <span className="text-sm font-semibold text-navy">Our courier has completed HIPAA & Bloodborne Pathogen training.</span>
          </div>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2">
          {certs.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.title} delay={i * 0.1}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.25 }}
                  data-testid={`cert-card-${i}`}
                  className="h-full rounded-3xl border border-navy/10 bg-white p-8 shadow-[0_8px_32px_rgba(11,46,89,0.05)]"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy">
                    <Icon className="h-7 w-7 text-gold" strokeWidth={1.9} />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-navy">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-navy/60">{c.desc}</p>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Testimonials ---------------- */
export const Testimonials = () => (
  <section data-testid="testimonials" className="relative overflow-hidden bg-navy py-24 lg:py-32">
    <div className="noise-overlay" />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
      <Reveal>
        <Eyebrow testid="testimonials-eyebrow">Testimonials</Eyebrow>
        <h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-white lg:text-5xl">
          Businesses across Atlanta trust us
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-6 lg:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div data-testid={`testimonial-${i}`} className="flex h-full flex-col rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm">
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className="h-5 w-5 fill-gold text-gold" />
                ))}
              </div>
              <p className="mt-6 flex-1 font-display text-xl font-medium leading-snug text-white">"{t.quote}"</p>
              <div className="mt-6 border-t border-white/10 pt-5">
                <p className="font-semibold text-white">{t.author}</p>
                <p className="text-sm text-white/60">{t.location}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
